import { useEffect, useRef, useState } from 'react';
import type { CompanionNotice } from '../lib/companion.js';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api.js';
import { captureMeeting, type MeetingCapture, type MeetingSource } from '../lib/meeting-capture.js';
import { ChunkUploader, extensionForMime, pickRecorderMime, type UploaderStatus } from '../lib/recorder.js';
import { mmss } from '../lib/format.js';
import { ErrorBox } from './ui.js';

type Phase = 'idle' | 'starting' | 'recording' | 'saving' | 'saved' | 'error';
interface Session {
  id: string;
  mime: string;
  recorder: MediaRecorder;
  capture: MeetingCapture;
  uploader: ChunkUploader;
  chunks: Blob[];
  startedAt: number;
  saving: boolean;
}

export function MeetingRecorder({ projectId, minimized, onMinimize, onClose, onNotice, onSaved }: {
  onSaved?: (id: string) => void; onNotice?: (notice: CompanionNotice | null) => void; projectId: string; minimized: boolean; onMinimize: () => void; onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [title, setTitle] = useState('');
  const [source, setSource] = useState<MeetingSource>('microphone');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [download, setDownload] = useState<{ url: string; extension: string } | null>(null);
  const [upload, setUpload] = useState<UploaderStatus>({ uploaded: 0, pending: 0, failing: false });
  const session = useRef<Session | null>(null);
  const abort = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const starting = useRef(false);
  const wake = useRef<WakeLockSentinel | null>(null);
  const busy = phase === 'starting' || phase === 'recording' || phase === 'saving';
  const supported = typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  const releaseWake = () => { void wake.current?.release().catch(() => undefined); wake.current = null; };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      abort.current?.abort();
      const s = session.current;
      if (s) {
        s.recorder.onstop = null;
        s.recorder.ondataavailable = null;
        if (s.recorder.state !== 'inactive') s.recorder.stop();
        s.capture.close();
        s.uploader.cancel();
      }
      releaseWake();
    };
  }, []);
  useEffect(() => () => { if (download) URL.revokeObjectURL(download.url); }, [download]);
  useEffect(() => {
    if (!busy && !(phase === 'error' && download)) return;
    const beforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [busy, phase, download]);
  useEffect(() => {
    if (phase !== 'recording') return;
    let alive = true;
    const lock = async () => {
      try {
        if (document.visibilityState !== 'visible' || !('wakeLock' in navigator) || wake.current) return;
        const sentinel = await navigator.wakeLock.request('screen');
        if (!alive) { await sentinel.release(); return; }
        wake.current = sentinel;
        sentinel.addEventListener('release', () => { if (wake.current === sentinel) wake.current = null; });
      } catch { /* Wake lock is best effort. */ }
    };
    void lock();
    document.addEventListener('visibilitychange', lock);
    const timer = setInterval(() => { if (session.current) setElapsed(Math.floor((Date.now() - session.current.startedAt) / 1000)); }, 500);
    return () => { alive = false; clearInterval(timer); document.removeEventListener('visibilitychange', lock); releaseWake(); };
  }, [phase]);

  useEffect(() => {
    if (phase === 'starting' || phase === 'saving') onNotice?.(null);
    if (phase === 'error' || notice) onNotice?.({ id: `recorder:${phase}:${error ?? notice}`, tone: 'attention', label: 'Your recording needs attention' });
    if (phase === 'saved' && session.current) onSaved?.(session.current.id);
  }, [phase, error, notice, onNotice, onSaved]);

  const save = async (s: Session, retry = false) => {
    if (s.saving) return;
    s.saving = true;
    setPhase('saving');
    setError(null);
    try {
      if (!s.chunks.length) throw new Error('No audio was captured. Start a new recording.');
      if (retry) {
        // A lost response to complete may already have queued the recording.
        const current = await api.recordings.get(s.id);
        if (current.recording.status !== 'uploaded') { if (mounted.current) setPhase('saved'); return; }
        s.uploader.cancel();
        await s.uploader.drain().catch(() => undefined);
        s.uploader = makeUploader(s.id);
        s.chunks.forEach((chunk, seq) => s.uploader.enqueue(seq, chunk));
      }
      await s.uploader.drain();
      if (!mounted.current) return;
      await api.recordings.complete(s.id, { chunkCount: s.chunks.length });
      if (mounted.current) setPhase('saved');
    } catch (err) {
      if (mounted.current) { setError(`${errorMessage(err)}${s.chunks.length ? ' Your audio backup is available below. Keep this tab open to retry saving.' : ''}`); setPhase('error'); }
    } finally { s.saving = false; }
  };
  const makeUploader = (id: string) => new ChunkUploader({
    upload: (seq, blob, signal) => api.recordings.putChunk(id, seq, blob, signal).then(() => undefined),
    maxAttempts: 5,
    onStatus: (status) => { if (mounted.current) setUpload(status); },
  });
  const stop = () => {
    const s = session.current;
    if (!s || s.recorder.state === 'inactive') return;
    setPhase('saving');
    s.recorder.stop();
  };
  const start = async () => {
    if (starting.current || session.current) return;
    starting.current = true;
    setPhase('starting'); setError(null); setNotice(null);
    const controller = new AbortController();
    abort.current = controller;
    let capture: MeetingCapture | undefined;
    try {
      const mime = pickRecorderMime();
      if (!mime) throw new Error('This browser cannot record audio. Upload an audio file from Recordings instead.');
      capture = await captureMeeting(source, controller.signal);
      const { recordingId } = await api.recordings.create(projectId, { title: title.trim() || `Meeting ${new Date().toLocaleString()}`, source: 'browser', mime });
      if (controller.signal.aborted) { capture.close(); return; }
      if (capture.tracks.some((t) => t.readyState === 'ended')) throw new Error('The audio source disconnected before recording started.');
      const mr = new MediaRecorder(capture.stream, { mimeType: mime });
      const s: Session = { id: recordingId, mime, recorder: mr, capture, uploader: makeUploader(recordingId), chunks: [], startedAt: Date.now(), saving: false };
      session.current = s;
      mr.ondataavailable = (event) => {
        if (!event.data.size) return;
        const seq = s.chunks.length;
        s.chunks.push(event.data);
        try { s.uploader.enqueue(seq, event.data); } catch { /* Keep collecting the backup after upload exhaustion. */ }
      };
      mr.onstop = () => {
        s.capture.close(); releaseWake();
        if (!mounted.current) return;
        setElapsed(Math.floor((Date.now() - s.startedAt) / 1000));
        if (s.chunks.length) setDownload({ url: URL.createObjectURL(new Blob(s.chunks, { type: mime })), extension: extensionForMime(mime) });
        void save(s);
      };
      mr.onerror = () => { setNotice('The audio recorder encountered an error. Saving the audio captured so far.'); stop(); };
      for (const track of capture.tracks) track.addEventListener('ended', () => {
        if (mr.state !== 'inactive') { setNotice('An audio source disconnected. Saving the audio captured so far.'); stop(); }
      }, { once: true });
      mr.start(15_000);
      setPhase('recording');
    } catch (err) {
      capture?.close();
      session.current?.uploader.cancel();
      session.current = null;
      if (mounted.current) { setError(errorMessage(err)); setPhase('error'); }
    } finally { starting.current = false; }
  };

  return (
    <aside className={`meeting-panel ${minimized ? 'minimized' : ''}`} aria-label="Meeting recorder">
      <div className="meeting-panel-head">
        <strong>{phase === 'recording' ? `Recording ${mmss(elapsed)}` : phase === 'saving' ? 'Saving meeting…' : 'Meeting recorder'}</strong>
        <button type="button" className="link-btn small" onClick={onMinimize}>{minimized ? 'Expand' : 'Minimize'}</button>
      </div>
      <div hidden={minimized}>
        <p className="muted tiny">Saved to <Link to={`/p/${projectId}/recordings`}>this project’s recordings</Link>. You can keep chatting while recording.</p>
        {notice ? <p role="status" className="small">{notice}</p> : null}
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        {!session.current ? <div className="stack">
          <label>Meeting title<input aria-label="Meeting title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} disabled={busy} /></label>
          <label>Audio source<select aria-label="Audio source" value={source} onChange={(e) => setSource(e.target.value as MeetingSource)} disabled={busy}>
            <option value="microphone">Microphone · people in the room</option>
            <option value="meeting" disabled={!navigator.mediaDevices?.getDisplayMedia}>Microphone + shared meeting audio</option>
          </select></label>
          <p className="muted small">{source === 'meeting' ? 'Select your meeting tab and enable Share audio. Use headphones. Screen video is not saved.' : 'Captures your microphone. Remote voices through headphones will not be recorded.'} Let everyone know before recording.</p>
          <button type="button" className="btn btn-primary" disabled={!supported || busy} onClick={() => void start()}>{phase === 'starting' ? 'Opening audio…' : 'Start recording'}</button>
          {!supported ? <p className="muted small">Recording is unavailable here. Use a supported browser on HTTPS, or upload an audio file.</p> : null}
        </div> : null}
        {phase === 'recording' || phase === 'saving' ? <p className="rec-banner" role="status"><span className="rec-dot" /> {phase === 'recording' ? `Recording ${mmss(elapsed)}` : 'Saving audio'} · {upload.uploaded} chunks saved{upload.pending ? ` · ${upload.pending} pending` : ''}{upload.failing ? ' · retrying upload' : ''}. Keep this tab open.</p> : null}
        {phase === 'recording' ? <button type="button" className="btn btn-stop" onClick={stop}>Stop and transcribe</button> : null}
        {phase === 'saved' ? <p role="status">Audio saved. <Link to={`/r/${session.current?.id}`}>View transcription status and review with the PM</Link>.</p> : null}
        <div className="row wrap meeting-panel-footer">
          {download ? <a className="btn btn-small" href={download.url} download={`meeting.${download.extension}`}>Download audio backup</a> : null}
          {phase === 'error' && session.current && download ? <button type="button" className="btn btn-small" onClick={() => { if (session.current) void save(session.current, true); }}>Retry saving</button> : null}
          {!busy ? <button type="button" className="link-btn small" onClick={onClose}>{phase === 'error' && download ? 'Discard unsaved recording' : 'Close'}</button> : null}
        </div>
      </div>
    </aside>
  );
}
